var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BufferAttribute, BufferGeometry, DataTexture, FloatType, Mesh, MeshBasicMaterial, MeshNormalMaterial, PerspectiveCamera, PlaneGeometry, Points, RGBAFormat, Raycaster, Scene, ShaderMaterial, SphereGeometry, TextureLoader, Vector2, Vector3, WebGLRenderer, } from 'three';
import { loadImage } from './utiils/ImageLoader';
import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer.js';
import fragment from './shaders/fragment';
import vertex from './shaders/vertex';
import simFragmentForces from './shaders/simFragmentForces';
import simFragmentPosition from './shaders/simFragmentPosition';
export class Particles {
    constructor({ container, imagePath, options, }) {
        this.revealed = false;
        this.renderProcessId = null;
        this.minGravity = 0.005;
        this.maxGravity = 0.07;
        this.mouseRadius = 0.09;
        this.friction = 0.9;
        if (!container) {
            throw new Error('Particles element requires container');
        }
        this.container = container;
        this.scene = new Scene();
        this.particleSize = options.particleSize;
        this.particlesAmount = options.particlesAmount;
        this.minGravity = options.minGravity || this.minGravity;
        this.maxGravity = options.maxGravity || this.maxGravity;
        this.mouseRadius = options.repelRadius || this.mouseRadius;
        this.friction = options.friction || this.friction;
        this.width = this.container.offsetWidth;
        this.height = this.container.offsetHeight;
        this.raycaster = new Raycaster();
        this.pointer = new Vector2();
        this.renderer = new WebGLRenderer({
            alpha: true,
            antialias: true,
        });
        this.renderer.setSize(this.width, this.height);
        this.container.appendChild(this.renderer.domElement);
        this.camera = new PerspectiveCamera(70, this.width / this.height, 0.01, 10);
        this.camera.position.z = 1;
        this.imagePath = imagePath;
        this.renderer.render(this.scene, this.camera);
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.getPixelDataFromImage(this.imagePath).then(({ dataPosition, dataPositionReveal, dataForces }) => {
                this.originalPositionData = dataPosition;
                this.positionRevealData = dataPositionReveal;
                this.forcesData = dataForces;
                this.initGPGPU();
                this.mouseEvents();
                this.addObjects();
            });
        });
    }
    getPixelDataFromImage(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const image = yield loadImage(url);
            const imageWidth = image.width;
            const imageHeight = image.height;
            const aspectRatio = imageWidth / imageHeight;
            const totalArea = imageWidth * imageHeight;
            const newArea = this.particlesAmount;
            if (newArea) {
                const scalingFactor = Math.sqrt(newArea / totalArea);
                this.imageWidth = Math.round(imageWidth * scalingFactor);
                this.imageHeight = Math.round(imageHeight * scalingFactor);
                if (this.imageWidth / this.imageHeight > aspectRatio) {
                    this.imageWidth = Math.round(this.imageHeight * aspectRatio);
                }
                else {
                    this.imageHeight = Math.round(this.imageWidth / aspectRatio);
                }
            }
            else {
                this.imageWidth = imageWidth;
                this.imageHeight = imageHeight;
            }
            this.number = this.imageWidth * this.imageHeight;
            const positionData = new Float32Array(4 * this.number);
            const positionRevealData = new Float32Array(4 * this.number);
            const forcesData = new Float32Array(4 * this.number);
            for (let i = 0; i < this.imageHeight; i++) {
                for (let j = 0; j < this.imageWidth; j++) {
                    const index = i * this.imageWidth + j;
                    let x = j / (this.imageWidth);
                    let y = i / (this.imageHeight);
                    x = (x - 0.5) * aspectRatio;
                    y = y - 0.5;
                    positionData[4 * index] = x;
                    positionData[4 * index + 1] = y;
                    const distanceX = x;
                    const distanceY = y;
                    const distance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2));
                    const normalizeX = distanceX / distance;
                    const normalizeY = distanceY / distance;
                    const moveDistance = 1;
                    let newX = x + normalizeX * moveDistance;
                    let newY = y + normalizeY * moveDistance;
                    const randomOffset = 0.5;
                    newX += (Math.random() - 0.5) * randomOffset;
                    newY += (Math.random() - 0.5) * randomOffset;
                    positionRevealData[4 * index] = newX;
                    positionRevealData[4 * index + 1] = newY;
                    forcesData[4 * index] = 0;
                    forcesData[4 * index + 1] = 0;
                    forcesData[4 * index + 2] = 0.005 + Math.random() * this.minGravity;
                    forcesData[4 * index + 3] = 0.01 + Math.random() * this.maxGravity;
                }
            }
            const positionDataTexture = new DataTexture(positionData, this.imageWidth, this.imageHeight, RGBAFormat, FloatType);
            positionDataTexture.needsUpdate = true;
            const positionRevealDataTexture = new DataTexture(positionRevealData, this.imageWidth, this.imageHeight, RGBAFormat, FloatType);
            positionRevealDataTexture.needsUpdate = true;
            const forcesDataTexture = new DataTexture(forcesData, this.imageWidth, this.imageHeight, RGBAFormat, FloatType);
            forcesDataTexture.needsUpdate = true;
            return {
                dataPosition: positionDataTexture,
                dataPositionReveal: positionRevealDataTexture,
                dataForces: forcesDataTexture,
            };
        });
    }
    mouseEvents() {
        this.planeMesh = new Mesh(new PlaneGeometry(10, 10), new MeshBasicMaterial({}));
        this.dummy = new Mesh(new SphereGeometry(0.01, 32, 32), new MeshNormalMaterial());
        this.dummy.position.copy(new Vector3(10, 10, 0));
        this.handleMouseMove = (e) => {
            const canvas = this.renderer.domElement;
            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            this.pointer.x = (x / canvas.clientWidth) * 2 - 1;
            this.pointer.y = -(y / canvas.clientHeight) * 2 + 1;
            this.raycaster.setFromCamera(this.pointer, this.camera);
            const intersects = this.raycaster.intersectObjects([this.planeMesh]);
            if (intersects.length > 0) {
                this.dummy.position.copy(intersects[0].point);
                this.positionUniforms.uMouse.value = intersects[0].point;
                this.forcesUniforms.uMouse.value = intersects[0].point;
            }
        };
        window.addEventListener('mousemove', this.handleMouseMove);
    }
    initGPGPU() {
        this.gpuCompute = new GPUComputationRenderer(this.imageWidth, this.imageHeight, this.renderer);
        this.forcesVariable = this.gpuCompute.addVariable('uCurrentForces', simFragmentForces, this.forcesData);
        this.positionVariable = this.gpuCompute.addVariable('uCurrentPosition', simFragmentPosition, this.originalPositionData);
        this.gpuCompute.setVariableDependencies(this.forcesVariable, [this.forcesVariable, this.positionVariable]);
        this.gpuCompute.setVariableDependencies(this.positionVariable, [this.positionVariable, this.forcesVariable]);
        this.positionUniforms = this.positionVariable.material.uniforms;
        this.forcesUniforms = this.forcesVariable.material.uniforms;
        this.positionUniforms.uTime = { value: 0 };
        this.positionUniforms.uMouse = { value: new Vector3(10, 10, 0) };
        this.positionUniforms.uOriginalPosition = { value: this.originalPositionData };
        this.positionUniforms.uRevealPosition = { value: this.positionRevealData };
        this.positionUniforms.uRevealProgress = { value: 0 };
        this.forcesUniforms.uMouse = { value: new Vector3(10, 10, 0) };
        this.forcesUniforms.uOriginalPosition = { value: this.originalPositionData };
        this.forcesUniforms.uRadius = { value: this.mouseRadius };
        this.forcesUniforms.uFriction = { value: this.friction };
        this.gpuCompute.init();
    }
    reveal() {
        if (!this.revealed) {
            this.render();
            this.revealed = true;
        }
        this.positionUniforms.uRevealProgress.value = 1;
        setTimeout(() => {
            this.positionUniforms.uRevealProgress.value = 0;
        }, 100);
    }
    addObjects() {
        this.geometry = new BufferGeometry();
        const positions = new Float32Array(this.number * 3);
        const uvs = new Float32Array(this.number * 2);
        const indices = Array.from({ length: this.imageHeight * this.imageWidth }, (_, i) => i);
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        for (let i = 0; i < this.imageHeight; i++) {
            for (let j = 0; j < this.imageWidth; j++) {
                const originalIndex = i * this.imageWidth + j;
                const shuffledIndex = indices[originalIndex];
                positions[3 * shuffledIndex] = j / this.imageWidth - 0.5;
                positions[3 * shuffledIndex + 1] = i / this.imageHeight - 0.5;
                positions[3 * shuffledIndex + 2] = 0;
                uvs[2 * shuffledIndex] = j / (this.imageWidth - 1);
                uvs[2 * shuffledIndex + 1] = i / (this.imageHeight - 1);
            }
        }
        this.geometry.setAttribute('position', new BufferAttribute(positions, 3));
        this.geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
        const texture = new TextureLoader().load(this.imagePath);
        this.material = new ShaderMaterial({
            uniforms: {
                uTextureMain: { value: texture },
                uNmColumns: { value: this.imageHeight },
                uNmLines: { value: this.imageWidth },
                uMainPosition: { value: null },
                uParticleSize: { value: this.particleSize },
            },
            vertexShader: vertex,
            fragmentShader: fragment,
            transparent: true,
        });
        this.points = new Points(this.geometry, this.material);
        this.scene.add(this.points);
    }
    resize() {
        this.width = this.container.offsetWidth;
        this.height = this.container.offsetHeight;
        this.renderer.setSize(this.width, this.height);
        this.camera.aspect = this.width / this.height;
        this.camera.updateProjectionMatrix();
    }
    render() {
        this.gpuCompute.compute();
        this.renderer.render(this.scene, this.camera);
        this.material.uniforms.uMainPosition.value = this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture;
        this.renderProcessId = window.requestAnimationFrame(this.render.bind(this));
    }
    pauseAnimation(delay) {
        if (typeof delay === 'number') {
            setTimeout(() => {
                if (this.renderProcessId) {
                    cancelAnimationFrame(this.renderProcessId);
                    this.renderProcessId = null;
                }
            }, delay);
        }
        else {
            if (this.renderProcessId) {
                cancelAnimationFrame(this.renderProcessId);
                this.renderProcessId = null;
            }
        }
    }
    destroy() {
        if (this.revealed) {
            cancelAnimationFrame(this.animationFrameId);
        }
        if (this.renderProcessId) {
            cancelAnimationFrame(this.renderProcessId);
        }
        window.removeEventListener('mousemove', this.handleMouseMove);
        this.scene.remove(this.points);
        this.scene.remove(this.planeMesh);
        this.scene.remove(this.dummy);
        if (this.container && this.renderer.domElement) {
            this.container.removeChild(this.renderer.domElement);
        }
    }
}
//# sourceMappingURL=Particles.js.map